/**
 * API Distribution Service Implementation
 * 
 * This service provides the API implementation for distribution operations,
 * communicating with the backend distribution endpoints to manage assessment
 * distribution, recipient tracking, and engagement analytics.
 * 
 * @module ApiDistributionService
 * @implements {IDistributionService}
 * 
 * Service Description:
 * The ApiDistributionService handles all distribution-related API calls including:
 * - Distribution creation and management
 * - Recipient tracking and status monitoring
 * - Email tracking (opens, clicks)
 * - Shareable link generation
 * - Reminder scheduling
 * - Distribution analytics and reporting
 * 
 * @requires ApiClient for HTTP requests
 * @requires IDistributionService interface
 */

import { AxiosInstance } from 'axios';
import { 
  IDistributionService 
} from '../../interfaces/IDistributionService';
import { 
  DistributionSettings, 
  DistributionStatus, 
  ShareableLink,
  RecipientStatus 
} from '../../distributionService';
import { BaseService } from '../base/BaseService';
import ApiClient from '../../../utils/apiClient';

export class ApiDistributionService extends BaseService implements IDistributionService {
  private api: AxiosInstance;
  private baseURL: string = '/api/v1/assessments/';

  constructor() {
    super('ApiDistributionService');
    this.api = ApiClient.getInstance();
    console.log('[ApiDistributionService] Initialized with base URL:', this.baseURL);
  }

  // ==================== Distribution Operations ====================
  
  async createDistribution(
    assessmentId: string, 
    settings: DistributionSettings
  ): Promise<DistributionStatus> {
    try {
      console.log(`[ApiDistributionService] Creating distribution for assessment ${assessmentId}`);
      
      // Transform settings to backend format
      const apiData = this.transformDistributionSettingsForAPI(settings);
      
      const response = await this.api.post(
        `${this.baseURL}${assessmentId}/distribute/`, 
        apiData
      );
      
      console.log('[ApiDistributionService] Distribution created:', response.data.id);
      return this.transformDistributionStatusFromAPI(response.data);
    } catch (error) {
      this.handleError(error, 'creating distribution');
    }
  }

  async getDistributionStatus(distributionId: string): Promise<DistributionStatus> {
    try {
      console.log(`[ApiDistributionService] Fetching distribution status ${distributionId}`);
      
      const response = await this.api.get(
        `${this.baseURL}distributions/${distributionId}/`
      );
      
      return this.transformDistributionStatusFromAPI(response.data);
    } catch (error) {
      this.handleError(error, `fetching distribution status ${distributionId}`);
    }
  }

  async getDistributionHistory(assessmentId: string): Promise<DistributionStatus[]> {
    try {
      console.log(`[ApiDistributionService] Fetching distribution history for assessment ${assessmentId}`);
      
      const response = await this.api.get(
        `${this.baseURL}${assessmentId}/distributions/`
      );
      
      const distributions = response.data.results || response.data;
      return distributions.map((dist: any) => 
        this.transformDistributionStatusFromAPI(dist)
      );
    } catch (error) {
      this.handleError(error, 'fetching distribution history');
    }
  }

  async previewDistribution(
    assessmentId: string,
    settings: DistributionSettings
  ): Promise<any> {
    try {
      console.log(`[ApiDistributionService] Previewing distribution for assessment ${assessmentId}`);
      
      const apiData = this.transformDistributionSettingsForAPI(settings);
      
      const response = await this.api.post(
        `${this.baseURL}${assessmentId}/distribution-preview/`, 
        apiData
      );
      
      return {
        recipientCount: response.data.recipient_count || response.data.recipientCount,
        recipients: response.data.recipients || [],
        estimatedCost: response.data.estimated_cost || response.data.estimatedCost
      };
    } catch (error) {
      this.handleError(error, 'previewing distribution');
    }
  }

  // ==================== Tracking Features ====================
  
  async sendReminders(distributionId: string): Promise<void> {
    try {
      console.log(`[ApiDistributionService] Sending reminders for distribution ${distributionId}`);
      
      await this.api.post(
        `${this.baseURL}distributions/${distributionId}/send-reminders/`
      );
      
      console.log('[ApiDistributionService] Reminders sent successfully');
    } catch (error) {
      this.handleError(error, 'sending reminders');
    }
  }

  async getRecipientStatuses(
    distributionId: string,
    filters?: any
  ): Promise<{
    results: RecipientStatus[];
    total: number;
    page: number;
    limit: number;
  }> {
    try {
      console.log(`[ApiDistributionService] Fetching recipient statuses for distribution ${distributionId}`);
      
      const params = {
        page: filters?.page || 1,
        limit: filters?.limit || 25,
        status: filters?.status,
        search: filters?.search
      };
      
      const response = await this.api.get(
        `${this.baseURL}distributions/${distributionId}/recipients/`,
        { params }
      );
      
      const recipients = response.data.results.map((recipient: any) => 
        this.transformRecipientStatusFromAPI(recipient)
      );
      
      return {
        results: recipients,
        total: response.data.count || recipients.length,
        page: params.page,
        limit: params.limit
      };
    } catch (error) {
      this.handleError(error, 'fetching recipient statuses');
    }
  }

  // ==================== Shareable Link Management ====================
  
  async generateShareableLink(
    assessmentId: string,
    settings: ShareableLink['settings']
  ): Promise<ShareableLink> {
    try {
      console.log(`[ApiDistributionService] Generating shareable link for assessment ${assessmentId}`);
      
      const apiData = {
        require_auth: settings.requireAuth,
        allow_anonymous: settings.allowAnonymous,
        single_use: settings.singleUse,
        max_uses: settings.maxUses,
        expires_at: settings.expiresAt?.toISOString()
      };
      
      const response = await this.api.post(
        `${this.baseURL}${assessmentId}/share/`, 
        apiData
      );
      
      console.log('[ApiDistributionService] Shareable link created:', response.data.id);
      return this.transformShareableLinkFromAPI(response.data);
    } catch (error) {
      this.handleError(error, 'generating shareable link');
    }
  }

  async getShareableLinks(assessmentId: string): Promise<ShareableLink[]> {
    try {
      console.log(`[ApiDistributionService] Fetching shareable links for assessment ${assessmentId}`);
      
      const response = await this.api.get(
        `${this.baseURL}${assessmentId}/links/`
      );
      
      const links = response.data.results || response.data;
      return links.map((link: any) => 
        this.transformShareableLinkFromAPI(link)
      );
    } catch (error) {
      this.handleError(error, 'fetching shareable links');
    }
  }

  async revokeShareableLink(linkId: string): Promise<void> {
    try {
      console.log(`[ApiDistributionService] Revoking shareable link ${linkId}`);
      
      await this.api.delete(
        `${this.baseURL}links/${linkId}/`
      );
      
      console.log('[ApiDistributionService] Shareable link revoked successfully');
    } catch (error) {
      this.handleError(error, `revoking shareable link ${linkId}`);
    }
  }

  // ==================== Export Functionality ====================
  
  async exportDistributionReport(
    distributionId: string, 
    format: 'pdf' | 'csv' | 'xlsx'
  ): Promise<Blob> {
    try {
      console.log(`[ApiDistributionService] Exporting distribution report ${distributionId} as ${format}`);
      
      const response = await this.api.get(
        `${this.baseURL}distributions/${distributionId}/export/`,
        {
          params: { format },
          responseType: 'blob' // Critical for binary data
        }
      );
      
      console.log(`[ApiDistributionService] Export completed, size: ${response.data.size} bytes`);
      return response.data;
    } catch (error) {
      this.handleError(error, 'exporting distribution report');
    }
  }

  // ==================== Private Helper Methods ====================
  
  private transformDistributionSettingsForAPI(settings: DistributionSettings): any {
    return {
      // Channel configuration
      channels: settings.channels.map(channel => ({
        type: channel.type,
        enabled: channel.enabled,
        config: channel.config
      })),
      
      // Target configuration
      target_type: settings.target.type,
      group_ids: settings.target.groups,
      individual_ids: settings.target.individuals,
      ministry_ids: settings.target.ministries,
      role_ids: settings.target.roles,
      
      // Message configuration
      subject: settings.message.subject,
      body: settings.message.body,
      include_instructions: settings.message.includeInstructions,
      custom_instructions: settings.message.customInstructions,
      
      // Schedule configuration
      send_immediately: settings.schedule.sendImmediately,
      scheduled_date: settings.schedule.scheduledDate?.toISOString(),
      timezone: settings.schedule.timezone,
      
      // Reminder configuration
      reminders_enabled: settings.reminders.enabled,
      reminder_frequency: settings.reminders.frequency,
      max_reminders: settings.reminders.maxReminders,
      custom_reminder_schedule: settings.reminders.customSchedule?.map(date => date.toISOString()),
      
      // Options
      allow_anonymous: settings.options.allowAnonymous,
      require_authentication: settings.options.requireAuthentication,
      track_opens: settings.options.trackOpens,
      track_clicks: settings.options.trackClicks,
      expires_at: settings.options.expiresAt?.toISOString()
    };
  }

  private transformDistributionStatusFromAPI(data: any): DistributionStatus {
    return {
      id: data.id,
      assessmentId: data.assessment_id || data.assessment,
      status: data.status,
      statistics: {
        totalRecipients: data.total_recipients || data.statistics?.totalRecipients || 0,
        sent: data.sent || data.statistics?.sent || 0,
        delivered: data.delivered || data.statistics?.delivered || 0,
        opened: data.opened || data.statistics?.opened || 0,
        clicked: data.clicked || data.statistics?.clicked || 0,
        responded: data.responded || data.statistics?.responded || 0,
        bounced: data.bounced || data.statistics?.bounced || 0,
        failed: data.failed || data.statistics?.failed || 0
      },
      startedAt: data.started_at ? new Date(data.started_at) : undefined,
      completedAt: data.completed_at ? new Date(data.completed_at) : undefined,
      errors: data.errors
    };
  }

  private transformRecipientStatusFromAPI(data: any): RecipientStatus {
    return {
      id: data.id,
      recipientId: data.recipient_id || data.recipient,
      recipientEmail: data.recipient_email || data.email,
      recipientName: data.recipient_name || data.name,
      status: data.status,
      sentAt: data.sent_at ? new Date(data.sent_at) : undefined,
      deliveredAt: data.delivered_at ? new Date(data.delivered_at) : undefined,
      openedAt: data.opened_at ? new Date(data.opened_at) : undefined,
      clickedAt: data.clicked_at ? new Date(data.clicked_at) : undefined,
      respondedAt: data.responded_at ? new Date(data.responded_at) : undefined,
      error: data.error || data.error_message
    };
  }

  private transformShareableLinkFromAPI(data: any): ShareableLink {
    return {
      id: data.id,
      assessmentId: data.assessment_id || data.assessment,
      url: data.url,
      shortUrl: data.short_url,
      qrCode: data.qr_code,
      settings: {
        requireAuth: data.require_auth,
        allowAnonymous: data.allow_anonymous,
        singleUse: data.single_use,
        maxUses: data.max_uses,
        expiresAt: data.expires_at ? new Date(data.expires_at) : undefined
      },
      statistics: {
        views: data.views || data.statistics?.views || 0,
        responses: data.responses || data.statistics?.responses || 0,
        uniqueVisitors: data.unique_visitors || data.statistics?.uniqueVisitors || 0
      },
      createdAt: new Date(data.created_at),
      createdBy: data.created_by
    };
  }

  private handleError(error: any, operation: string): never {
    console.error(`[ApiDistributionService] Error during ${operation}:`, error);
    
    // Enhanced error handling
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.detail || error.response.data?.message || error.message;
      
      switch (status) {
        case 400:
          throw new Error(`Invalid request: ${message}`);
        case 401:
          throw new Error('Authentication required');
        case 403:
          throw new Error('You do not have permission to perform this action');
        case 404:
          throw new Error('Resource not found');
        case 500:
          throw new Error('Server error. Please try again later');
        default:
          throw new Error(message || 'An unexpected error occurred');
      }
    }
    
    // Network or other errors
    throw new Error(error.message || 'Network error. Please check your connection');
  }
}